import React, { useState, useEffect } from 'react';
import { Sun, Building2, Users, ChevronDown, ChevronUp, Zap, ArrowRight, CheckCircle, TrendingUp, BarChart3, Calculator, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { isSupabaseConfigured } from '../lib/supabase';
import type { Database } from '../types/supabase';
import { toast } from 'react-hot-toast';
import { ContactModal } from '../components/ContactModal';
import { SEOHead } from '../components/SEOHead';
import { ServicesDiscoveryModal } from '../components/ServicesDiscoveryModal';
import { NetworkSphere } from '../components/NetworkSphere';
import { DailyConsumptionChart } from '../components/DailyConsumptionChart';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { EnergySharingDiagram } from '../components/EnergySharingDiagram';
import EntreprisePage from './EntreprisePage';
import ParticuliersPage from './ParticuliersPage';
import AdminPage from './AdminPage';

type Participant = Database['public']['Tables']['participants']['Row'];

interface HomePageProps {
  segment?: 'entreprises' | 'particuliers';
  autoOpenSimulator?: boolean;
}

export default function HomePage({ segment = 'entreprises', autoOpenSimulator = false }: HomePageProps) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallbackData, setUsingFallbackData] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSimulation, setShowSimulation] = useState(autoOpenSimulator);
  const [showProcedure, setShowProcedure] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [userType, setUserType] = useState<'producer' | 'consumer' | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    loadParticipants();
  }, []);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      setError(null);
      setUsingFallbackData(false);

      if (!isSupabaseConfigured || !supabase) {
        console.log('Supabase not configured, showing empty data');
        setParticipants([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('participants')
          .select('*')
          .order('name');

        if (error) {
          console.warn('Error loading participants:', error);
          setParticipants([]);
          return;
        }

        setParticipants(data || []);
      } catch (networkError) {
        console.warn('Network error loading participants:', networkError);
        setParticipants([]);
      }

    } catch (error: any) {
      console.warn('Error loading participants:', error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const producers = participants.filter(p => p.type === 'producer');
  const consumers = participants.filter(p => p.type === 'consumer');

  const faqItems = t('home.faq.items', { returnObjects: true }) as Array<{question: string, answer: string}>;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 mx-auto mb-4" style={{
            borderTop: '2px solid #F8BD3D',
            borderRight: '2px solid transparent',
            borderBottom: '2px solid transparent',
            borderLeft: '2px solid transparent'
          }}></div>
          <p className="font-sans" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (segment === 'entreprises') {
    return <EntreprisePage />;
  }

  if (segment === 'particuliers') {
    return <ParticuliersPage />;
  }

  return (
    <>
      <SEOHead
        title="Nos Services Entreprises | Sun Is Up - Communaute d'Energie & Optimisation Energetique Bruxelles"
        description="Services energetiques pour entreprises Bruxelles : communaute d'energie, optimisation contrats, etudes panneaux solaires, contrats groupes. Reduisez vos couts energetiques avec Sun Is Up."
        keywords="services energetiques entreprises bruxelles, communaute d'energie entreprises, optimisation contrat electricite belgique, etude panneaux solaires entreprise, contrat groupe energie belgique, reduction couts energetiques bruxelles, transition energetique entreprises belgique"
        url="https://sunisup.be"
        logo="https://sunisup.be/images/logo.png"
      />
      <div className="space-y-0 font-sans bg-white">
        <section className="relative min-h-screen flex items-start justify-center overflow-hidden pt-36 md:pt-24" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #000000 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-4 z-10">
            <div className="text-center mb-8 mt-4 sm:mt-0 md:mt-0">
              <div className="relative inline-block mb-6">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight font-sans tracking-tight px-2 relative z-10"
                    style={{
                      color: '#1a1a1a',
                      textShadow: '0 4px 12px rgba(248, 189, 61, 0.15)'
                    }}>
                  Sun Is Up
                </h1>
                <div className="absolute inset-0 -z-10 blur-3xl opacity-30"
                     style={{
                       background: 'radial-gradient(ellipse at center, #F8BD3D 0%, transparent 70%)',
                       transform: 'scale(1.2)'
                     }}
                />
              </div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl leading-relaxed font-sans font-light max-w-6xl mx-auto px-4" style={{ color: '#4a4a4a' }}>
                {t('home.hero.mainTitle')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-3xl p-4 sm:p-10 transition-all duration-300 lg:w-[150%]">
                <div className="flex items-center justify-start sm:justify-center mb-8">
                  <div className="inline-flex rounded-2xl p-1.5 relative shadow-md" style={{ backgroundColor: 'white', border: '2px solid #F8BD3D' }}>
                    <button
                      onClick={() => setUserType('consumer')}
                      className={`px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 relative z-10 font-sans text-xs sm:text-sm shadow-sm ${
                        userType === 'consumer' || userType === null
                          ? ''
                          : ''
                      }`}
                      style={
                        userType === 'consumer' || userType === null
                          ? { backgroundColor: '#F8BD3D', color: '#1a1a1a' }
                          : { backgroundColor: 'transparent', color: '#666666' }
                      }
                    >
                      {t('nav.consumer')}
                    </button>
                    <button
                      onClick={() => setUserType('producer')}
                      className={`px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 relative z-10 font-sans text-xs sm:text-sm shadow-sm ${
                        userType === 'producer'
                          ? ''
                          : ''
                      }`}
                      style={
                        userType === 'producer'
                          ? { backgroundColor: '#F8BD3D', color: '#1a1a1a' }
                          : { backgroundColor: 'transparent', color: '#666666' }
                      }
                    >
                      {t('nav.producer')}
                    </button>
                  </div>
                </div>

                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4" style={{ backgroundColor: '#F8BD3D' }}>
                    <Zap className="w-6 h-6" style={{ color: '#ffffff' }} />
                  </div>
                  <h3 className="text-3xl font-bold font-sans" style={{ color: '#1a1a1a' }}>
                    {t('home.hero.keyPoints.title')}
                  </h3>
                </div>

                {(userType === 'consumer' || userType === null) && (
                  <div className="space-y-3 sm:space-y-4">
                    {(t('home.hero.consumerBenefits', { returnObjects: true }) as string[]).map((benefit: string, index: number) => (
                      <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mt-0.5 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                        <p className="text-sm sm:text-base font-sans leading-relaxed" style={{ color: '#1a1a1a' }}>
                          {benefit}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {userType === 'producer' && (
                  <div className="space-y-3 sm:space-y-4">
                    {(t('home.hero.producerBenefits', { returnObjects: true }) as string[]).map((benefit: string, index: number) => (
                      <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mt-0.5 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                        <p className="text-sm sm:text-base font-sans leading-relaxed" style={{ color: '#1a1a1a' }}>
                          {benefit}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center lg:ml-[25%]">
                <NetworkSphere />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <button
                onClick={() => setShowProcedure(true)}
                className="px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1 inline-flex items-center justify-center gap-3 text-lg font-sans"
                style={{
                  backgroundColor: '#F8BD3D',
                  color: '#1a1a1a'
                }}
              >
                {t('home.hero.learnMore')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        <section id="services-section" className="py-11 sm:py-22" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-20">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 font-sans px-2" style={{ color: '#1a1a1a' }}>
                {t('home.hero.optimization.title')}
              </h2>
              <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4 text-left px-2">
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-sans" style={{ color: '#4a4a4a' }}>
                  {t('home.hero.optimization.paragraph1')}
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-sans" style={{ color: '#4a4a4a' }}>
                  {t('home.hero.optimization.paragraph2')}
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-sans" style={{ color: '#4a4a4a' }}>
                  {t('home.hero.optimization.paragraph3')}
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-sans" style={{ color: '#4a4a4a' }}>
                  {t('home.hero.optimization.paragraph4')}
                </p>
                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-sans font-semibold" style={{ color: '#F8BD3D' }}>
                  {t('home.hero.optimization.highlight')}
                </p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto space-y-8 mb-16">
              <div className="bg-white rounded-2xl p-10 transition-all duration-300 hover:shadow-2xl" style={{ border: '1px solid #e5e5e5' }}>
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0" style={{ backgroundColor: '#FFF5E1' }}>
                    <Users className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1 font-sans" style={{ color: '#1a1a1a' }}>{t('home.services.community.title')}</h3>
                  </div>
                </div>
                <p className="text-base leading-relaxed mb-8 font-sans" style={{ color: '#666666' }}>
                  {t('home.services.community.description')}
                </p>

                <div className="flex justify-center mb-8">
                  <EnergySharingDiagram />
                </div>

                <div className="mt-12 mb-8">
                  <BeforeAfterSlider />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setShowSimulation(true)}
                    className="px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 font-sans"
                    style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                  >
                    <Calculator className="w-5 h-5" />
                    {t('home.services.community.simulateButton')}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-10 transition-all duration-300 hover:shadow-2xl" style={{ border: '1px solid #e5e5e5' }}>
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0" style={{ backgroundColor: '#FFF5E1' }}>
                    <Building2 className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1 font-sans" style={{ color: '#1a1a1a' }}>{t('home.services.subCommunity.title')}</h3>
                  </div>
                </div>
                <p className="text-base leading-relaxed mb-8 font-sans" style={{ color: '#666666' }}>
                  {t('home.services.subCommunity.description')}
                </p>

                <div className="space-y-4 mb-8">
                  {t('home.services.subCommunity.features', { returnObjects: true }).map((feature: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                      <span className="text-base font-sans" style={{ color: '#666666' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 font-sans"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {t('home.services.subCommunity.button')}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-white rounded-2xl p-10 transition-all duration-300 hover:shadow-2xl" style={{ border: '1px solid #e5e5e5' }}>
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0" style={{ backgroundColor: '#FFF5E1' }}>
                    <BarChart3 className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1 font-sans" style={{ color: '#1a1a1a' }}>{t('home.services.platform.title')}</h3>
                  </div>
                </div>
                <p className="text-base leading-relaxed mb-8 font-sans" style={{ color: '#666666' }}>
                  {t('home.services.platform.description')}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="p-6 rounded-xl" style={{ backgroundColor: '#FFF5E1' }}>
                    <BarChart3 className="w-8 h-8 mx-auto mb-3" style={{ color: '#F8BD3D' }} />
                    <p className="text-sm font-medium text-center font-sans" style={{ color: '#1a1a1a' }}>Monitoring temps réel</p>
                  </div>
                  <div className="p-6 rounded-xl" style={{ backgroundColor: '#FFF5E1' }}>
                    <TrendingUp className="w-8 h-8 mx-auto mb-3" style={{ color: '#F8BD3D' }} />
                    <p className="text-sm font-medium text-center font-sans" style={{ color: '#1a1a1a' }}>Optimisation continue</p>
                  </div>
                </div>

                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 font-sans"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {t('home.services.platform.button')}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-white rounded-2xl p-10 transition-all duration-300 hover:shadow-2xl" style={{ border: '1px solid #e5e5e5' }}>
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0" style={{ backgroundColor: '#FFF5E1' }}>
                    <Sun className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1 font-sans" style={{ color: '#1a1a1a' }}>{t('home.services.optimization.title')}</h3>
                  </div>
                </div>
                <p className="text-base leading-relaxed mb-8 font-sans" style={{ color: '#666666' }}>
                  {t('home.services.optimization.description')}
                </p>

                <div className="space-y-4 mb-8">
                  {t('home.services.optimization.features', { returnObjects: true }).map((feature: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                      <span className="text-base font-sans" style={{ color: '#666666' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full px-6 py-3 rounded-full font-medium transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 font-sans"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {t('home.services.optimization.button')}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-22" style={{ backgroundColor: '#ffffff' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <DailyConsumptionChart />
          </div>
        </section>

        <section className="pt-22 pb-20 sm:pb-28 lg:pb-32" style={{ backgroundColor: '#ffffff' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6 font-sans tracking-tight" style={{ color: '#1a1a1a' }}>
                {t('home.faq.title')}
              </h2>
            </div>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white rounded-xl hover:shadow-lg transition-all duration-300" style={{ border: '1px solid #e5e5e5' }}>
                  <button
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    className="w-full flex items-center justify-between text-left p-6 transition-colors rounded-xl"
                  >
                    <h3 className="text-lg font-bold pr-4 font-sans" style={{ color: '#1a1a1a' }}>{item.question}</h3>
                    {expandedFAQ === index ? (
                      <ChevronUp className="w-6 h-6 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                    ) : (
                      <ChevronDown className="w-6 h-6 flex-shrink-0" style={{ color: '#999999' }} />
                    )}
                  </button>
                  {expandedFAQ === index && (
                    <div className="px-6 pb-6">
                      <p className="leading-relaxed text-base font-sans" style={{ color: '#666666' }}>{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pt-24 sm:pt-28 lg:pt-32 pb-17 relative overflow-hidden" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-sans" style={{ color: '#1a1a1a' }}>
              {t('home.energyCta.title')}
            </h2>
            <p className="text-lg lg:text-xl mb-10 max-w-3xl mx-auto font-sans font-light" style={{ color: '#4a4a4a' }}>
              {t('home.energyCta.description')}
            </p>
            <button
              onClick={() => setShowContactModal(true)}
              className="px-10 py-4 rounded-full font-semibold transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center gap-3 mx-auto text-lg font-sans"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              {t('home.energyCta.button')}
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </section>

        <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />

        <ServicesDiscoveryModal
          isOpen={showServicesModal}
          onClose={() => setShowServicesModal(false)}
        />

        {showSimulation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-neutral-300 p-4 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-xl font-bold text-neutral-900 font-sans">Simulation</h2>
                <button
                  onClick={() => setShowSimulation(false)}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-0">
                <SimulationPage />
              </div>
            </div>
          </div>
        )}

        {showProcedure && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-neutral-300 p-4 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-xl font-bold text-neutral-900 font-sans">Procédure d'adhésion</h2>
                <button
                  onClick={() => setShowProcedure(false)}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-0">
                <AdminPage />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
